import { useCallback, useEffect, useRef, type ReactElement } from 'react';

// Cloudflare Turnstile, for the forms that take a password or send an email. No
// site key means no script, no widget and no token, so an environment without one
// behaves as if this did not exist. The widget stays invisible unless Cloudflare
// wants the person to interact, and a token is fetched only at submit: a token works
// once and expires in five minutes, so one fetched on page load could be stale.

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_TIMEOUT_MS = 10_000;
const CHALLENGE_TIMEOUT_MS = 20_000;

export class CaptchaError extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = 'CaptchaError';
  }
}

interface TurnstileRenderOptions {
  sitekey: string;
  execution: 'execute';
  appearance: 'interaction-only';
  language: string;
  callback: (token: string) => void;
  'error-callback': () => boolean;
  'timeout-callback': () => void;
  'unsupported-callback': () => void;
  'before-interactive-callback': () => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

interface TurnstileWindow {
  turnstile?: TurnstileApi;
}

let scriptLoad: Promise<TurnstileApi> | null = null;

const loadTurnstile = (): Promise<TurnstileApi> => {
  const loaded = (window as TurnstileWindow).turnstile;
  if (loaded !== undefined) return Promise.resolve(loaded);
  if (scriptLoad !== null) return scriptLoad;

  const attempt = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    const timer = window.setTimeout(() => {
      script.remove();
      reject(new CaptchaError('script-timeout'));
    }, SCRIPT_TIMEOUT_MS);
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      window.clearTimeout(timer);
      const api = (window as TurnstileWindow).turnstile;
      if (api === undefined) reject(new CaptchaError('script-empty'));
      else resolve(api);
    };
    script.onerror = () => {
      window.clearTimeout(timer);
      script.remove();
      reject(new CaptchaError('script-failed'));
    };
    document.head.appendChild(script);
  });
  // A failed load must not stick: "Prueba otra vez" has to really try again.
  attempt.catch(() => {
    scriptLoad = null;
  });
  scriptLoad = attempt;
  return attempt;
};

export interface Captcha {
  // Render it in the form; null without a site key. Empty unless a challenge shows.
  readonly field: ReactElement | null;
  // A fresh token, or undefined without a site key. Rejects with CaptchaError.
  readonly getToken: () => Promise<string | undefined>;
}

interface PendingToken {
  resolve: (token: string) => void;
  reject: (error: CaptchaError) => void;
  timer: number | undefined;
}

// `language` is read when the widget is first drawn, which is the first submit.
export const useCaptcha = (
  siteKey: string | undefined,
  language: () => string = () => 'es',
): Captcha => {
  // Read through a ref, so a caller's inline function does not rebuild `getToken`.
  const currentLanguage = useRef(language);
  currentLanguage.current = language;
  const isEnabled = siteKey !== undefined && siteKey.trim().length > 0;
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  // Set when a panel holding the field closes and opens again with a new element.
  const widgetElement = useRef<HTMLElement | null>(null);
  const pending = useRef<PendingToken | null>(null);

  const settle = useCallback((action: (request: PendingToken) => void): void => {
    const request = pending.current;
    if (request === null) return;
    pending.current = null;
    window.clearTimeout(request.timer);
    action(request);
  }, []);

  // Fetching the script early keeps the submit that needs it quick. A failure here
  // is not reported: the submit tries again and says so if it fails too.
  useEffect(() => {
    if (isEnabled) void loadTurnstile().catch(() => undefined);
    return () => {
      settle((request) => request.reject(new CaptchaError('unmounted')));
      const id = widgetId.current;
      widgetId.current = null;
      if (id !== null) (window as TurnstileWindow).turnstile?.remove(id);
    };
  }, [isEnabled, settle]);

  const getToken = useCallback(async (): Promise<string | undefined> => {
    if (!isEnabled) return undefined;
    const api = await loadTurnstile();
    const element = container.current;
    if (element === null) throw new CaptchaError('no-container');

    return new Promise<string>((resolve, reject) => {
      settle((previous) => previous.reject(new CaptchaError('superseded')));
      pending.current = {
        resolve,
        reject,
        timer: window.setTimeout(() => {
          settle((request) => request.reject(new CaptchaError('challenge-timeout')));
        }, CHALLENGE_TIMEOUT_MS),
      };

      if (widgetId.current !== null && widgetElement.current !== element) {
        api.remove(widgetId.current);
        widgetId.current = null;
      }
      if (widgetId.current === null) {
        widgetElement.current = element;
        widgetId.current =
          api.render(element, {
            sitekey: siteKey,
            execution: 'execute',
            appearance: 'interaction-only',
            language: currentLanguage.current(),
            callback: (token) => settle((request) => request.resolve(token)),
            'error-callback': () => {
              settle((request) => request.reject(new CaptchaError('challenge-error')));
              // Handled: stops Turnstile retrying and logging behind the form.
              return true;
            },
            'timeout-callback': () =>
              settle((request) => request.reject(new CaptchaError('challenge-expired'))),
            'unsupported-callback': () =>
              settle((request) => request.reject(new CaptchaError('unsupported'))),
            // Someone is now solving it, and however long that takes is fine.
            'before-interactive-callback': () => {
              if (pending.current !== null) window.clearTimeout(pending.current.timer);
            },
          }) ?? null;
        if (widgetId.current === null) {
          settle((request) => request.reject(new CaptchaError('render-failed')));
          return;
        }
      } else {
        api.reset(widgetId.current);
      }
      api.execute(widgetId.current);
    });
  }, [isEnabled, siteKey, settle]);

  return { field: isEnabled ? <div ref={container} /> : null, getToken };
};
